import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Briefcase, Users, TrendingUp, MapPin, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { whyMIT } from '../mockData';

export const WhyMIT = () => {
  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-red-50 py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">¿Por qué estudiar en MIT?</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Somos una institución comprometida con la formación técnica profesional de calidad, con respaldo oficial y enfoque en la empleabilidad
          </p>
        </div>
      </section>

      {/* Principales Beneficios */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyMIT.map((item, index) => {
              const IconComponent = require('lucide-react')[item.icon];
              return (
                <Card key={index} className={`hover-lift animate-fade-in-up animate-delay-${(index % 3) * 100}`}>
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-mit-blue/10 rounded-xl flex items-center justify-center mb-6">
                      <IconComponent className="w-8 h-8 text-mit-blue" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Respaldo Legal e Institucional */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="w-20 h-20 bg-mit-red/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-10 h-10 text-mit-red" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Respaldo Legal e Institucional</h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                MIT Instituto de Formación Digital es una institución autorizada para otorgar títulos técnicos profesionales a nombre de la nación, con plena validez legal en todo el territorio peruano.
              </p>
            </div>

            <div className="space-y-6">
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-mit-blue flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Autorización Oficial</h3>
                      <p className="text-gray-700">
                        Institución autorizada por el Ministerio de Educación para ofrecer formación técnica profesional.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-mit-blue flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Título a Nombre de la Nación</h3>
                      <p className="text-gray-700">
                        Nuestros egresados obtienen un título oficial con reconocimiento nacional, válido para ejercer la profesión y postular a empleos públicos y privados.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-mit-blue flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Marco Normativo</h3>
                      <p className="text-gray-700">
                        Cumplimos con toda la normativa vigente en educación técnica profesional del Perú, garantizando calidad educativa.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Enfoque Práctico */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-mit-blue/10 rounded-xl flex items-center justify-center mb-6">
                <Briefcase className="w-8 h-8 text-mit-blue" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Formación Práctica y Real</h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Nuestra metodología educativa prioriza el aprendizaje práctico y la experiencia real, preparando profesionales listos para el mercado laboral.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-mit-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Talleres y Laboratorios Equipados</h4>
                    <p className="text-gray-600">Instalaciones con tecnología actualizada para prácticas profesionales</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-mit-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Prácticas en Empresas</h4>
                    <p className="text-gray-600">Convenios con empresas líderes del sector para prácticas pre-profesionales</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-mit-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Proyectos Reales</h4>
                    <p className="text-gray-600">Desarrollo de proyectos aplicados a situaciones laborales reales</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_mit-formacion/artifacts/h1unkne2_WhatsApp%20Image%202026-01-30%20at%204.06.44%20PM.jpeg"
                alt="Formación práctica"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Docentes */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_mit-formacion/artifacts/nt6lfyh2_WhatsApp%20Image%202026-01-30%20at%204.06.43%20PM%20%281%29.jpeg"
                alt="Docentes experimentados"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="w-16 h-16 bg-mit-red/10 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-mit-red" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Docentes con Experiencia Real</h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Nuestro equipo docente está conformado por profesionales en activo que combinan experiencia laboral con vocación de enseñanza.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-mit-red flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Profesionales en Activo</h4>
                    <p className="text-gray-600">Docentes que trabajan en su especialidad y comparten conocimiento actualizado</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-mit-red flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Mentores Profesionales</h4>
                    <p className="text-gray-600">Acompañamiento personalizado en tu proceso de formación</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-mit-red flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Red de Contactos</h4>
                    <p className="text-gray-600">Acceso a networking profesional a través de nuestros docentes</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Empleabilidad */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="w-20 h-20 bg-mit-blue/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <TrendingUp className="w-10 h-10 text-mit-blue" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Alta Empleabilidad</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Nuestros egresados se insertan rápidamente al mercado laboral gracias a su sólida formación técnica y experiencia práctica.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover-lift text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-mit-blue mb-3">85%</div>
                <p className="text-gray-700 font-semibold mb-2">de Empleabilidad</p>
                <p className="text-sm text-gray-600">Egresados trabajando en su especialidad</p>
              </CardContent>
            </Card>

            <Card className="hover-lift text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-mit-red mb-3">3 meses</div>
                <p className="text-gray-700 font-semibold mb-2">Tiempo Promedio</p>
                <p className="text-sm text-gray-600">Para conseguir empleo después de egresar</p>
              </CardContent>
            </Card>

            <Card className="hover-lift text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-mit-blue mb-3">50+</div>
                <p className="text-gray-700 font-semibold mb-2">Empresas Aliadas</p>
                <p className="text-sm text-gray-600">Convenios para prácticas y empleos</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comunidad Regional */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-red-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-mit-red/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-10 h-10 text-mit-red" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Compromiso con Arequipa y el Sur del Perú</h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Somos una institución con raíces en la región, comprometida con el desarrollo profesional de nuestra comunidad y el crecimiento económico del sur peruano.
            </p>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-2">Formación Contextualizada</h3>
                  <p className="text-gray-600">Programas adaptados a las necesidades del mercado laboral regional</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-2">Responsabilidad Social</h3>
                  <p className="text-gray-600">Participación activa en proyectos de desarrollo comunitario</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-mit-blue text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Forma Parte de Nuestra Comunidad</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Únete a cientos de profesionales que han transformado su futuro con MIT
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/carreras">
              <Button size="lg" className="bg-white text-mit-blue hover:bg-gray-100 w-full sm:w-auto">
                Ver Carreras
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contacto">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-mit-blue w-full sm:w-auto">
                Más Información
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};